export const STORY_TEAM_REQUIRED_MEMBER_KEY = "zay";
export const STORY_TEAM_BASE_SLOT_COUNT = 2;
export const STORY_TEAM_EXPANDED_SLOT_COUNT = 3;
export const STORY_TEAM_THIRD_SLOT_UNLOCK_LEVEL = 25;
export const STORY_ACCURACY_TOTAL_CAP = 8;

export const normalizeStoryTeamMemberKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const normalizeUnlockedStoryTeamRoster = (roster = []) => [
  ...new Set([
    STORY_TEAM_REQUIRED_MEMBER_KEY,
    ...(Array.isArray(roster) ? roster : []),
  ].map(normalizeStoryTeamMemberKey).filter(Boolean)),
];

const normalizeNumericStoryLevel = (level = 1) => {
  const rawLevel = String(level || "1").trim();
  if (/^m\d+$/i.test(rawLevel)) return Number.POSITIVE_INFINITY;
  const numericLevel = Number(rawLevel);
  return Number.isFinite(numericLevel) ? Math.max(1, Math.floor(numericLevel)) : 1;
};

export const getStoryTeamSlotCount = (storyLevel = 1) =>
  normalizeNumericStoryLevel(storyLevel) >= STORY_TEAM_THIRD_SLOT_UNLOCK_LEVEL
    ? STORY_TEAM_EXPANDED_SLOT_COUNT
    : STORY_TEAM_BASE_SLOT_COUNT;

export const resolveActiveStoryTeamRoster = ({
  storyTeamRoster = [],
  activeStoryTeamRoster,
  storyLevel = 1,
} = {}) => {
  const unlockedRoster = normalizeUnlockedStoryTeamRoster(storyTeamRoster);
  const slotCount = getStoryTeamSlotCount(storyLevel);
  const hasSavedLineup = Array.isArray(activeStoryTeamRoster);
  const requestedLineup = hasSavedLineup
    ? activeStoryTeamRoster
    : unlockedRoster.slice(0, Math.min(STORY_TEAM_BASE_SLOT_COUNT, slotCount));
  const normalizedLineup = [
    STORY_TEAM_REQUIRED_MEMBER_KEY,
    ...requestedLineup,
  ]
    .map(normalizeStoryTeamMemberKey)
    .filter((key, index, values) => key && values.indexOf(key) === index)
    .filter((key) => unlockedRoster.includes(key));

  return normalizedLineup.slice(0, slotCount);
};

export const resolveStoryBattleTeam = (storyState = null) => {
  const activeTeamRoster = resolveActiveStoryTeamRoster({
    storyTeamRoster: storyState?.storyTeamRoster,
    activeStoryTeamRoster: storyState?.activeStoryTeamRoster,
    storyLevel: storyState?.level,
  });
  const requestedLeaderKey = normalizeStoryTeamMemberKey(
    storyState?.activeStoryAvatarKey,
  );
  const leaderKey = activeTeamRoster.includes(requestedLeaderKey)
    ? requestedLeaderKey
    : STORY_TEAM_REQUIRED_MEMBER_KEY;

  return {
    activeTeamRoster,
    leaderKey,
    teammateKeys: activeTeamRoster.filter((key) => key !== leaderKey),
  };
};

export const toggleActiveStoryTeamMember = ({
  storyTeamRoster = [],
  activeStoryTeamRoster,
  activeStoryAvatarKey = STORY_TEAM_REQUIRED_MEMBER_KEY,
  storyLevel = 1,
  characterKey = "",
} = {}) => {
  const unlockedRoster = normalizeUnlockedStoryTeamRoster(storyTeamRoster);
  const currentLineup = resolveActiveStoryTeamRoster({
    storyTeamRoster: unlockedRoster,
    activeStoryTeamRoster,
    storyLevel,
  });
  const normalizedCharacterKey = normalizeStoryTeamMemberKey(characterKey);
  const normalizedActiveAvatarKey = normalizeStoryTeamMemberKey(
    activeStoryAvatarKey,
  );
  const currentActiveAvatarKey = currentLineup.includes(
    normalizedActiveAvatarKey,
  )
    ? normalizedActiveAvatarKey
    : STORY_TEAM_REQUIRED_MEMBER_KEY;

  if (normalizedCharacterKey === STORY_TEAM_REQUIRED_MEMBER_KEY) {
    return {
      changed: false,
      reason: "zay-required",
      activeStoryTeamRoster: currentLineup,
      activeStoryAvatarKey: currentActiveAvatarKey,
    };
  }

  if (!unlockedRoster.includes(normalizedCharacterKey)) {
    return {
      changed: false,
      reason: "character-locked",
      activeStoryTeamRoster: currentLineup,
      activeStoryAvatarKey: currentActiveAvatarKey,
    };
  }

  if (currentLineup.includes(normalizedCharacterKey)) {
    const nextLineup = currentLineup.filter(
      (key) => key !== normalizedCharacterKey,
    );
    return {
      changed: true,
      reason: "removed",
      activeStoryTeamRoster: nextLineup,
      activeStoryAvatarKey:
        currentActiveAvatarKey === normalizedCharacterKey
          ? STORY_TEAM_REQUIRED_MEMBER_KEY
          : currentActiveAvatarKey,
    };
  }

  if (currentLineup.length >= getStoryTeamSlotCount(storyLevel)) {
    return {
      changed: false,
      reason: "team-full",
      activeStoryTeamRoster: currentLineup,
      activeStoryAvatarKey: currentActiveAvatarKey,
    };
  }

  return {
    changed: true,
    reason: "added",
    activeStoryTeamRoster: [...currentLineup, normalizedCharacterKey],
    activeStoryAvatarKey: currentActiveAvatarKey,
  };
};

export const canApplyStoryCharacterBoost = ({
  statKey = "",
  baseStatValue = null,
  currentBoostValue = 0,
} = {}) => {
  if (String(statKey || "").trim().toLowerCase() !== "accuracy") return true;

  const baseAccuracy = Number(baseStatValue);
  const accuracyBoost = Math.max(0, Number(currentBoostValue) || 0);
  if (!Number.isFinite(baseAccuracy)) return false;

  return baseAccuracy + accuracyBoost < STORY_ACCURACY_TOTAL_CAP;
};
